import { zodResolver } from '@hookform/resolvers/zod';
import { Truck, Home, Search, AlertTriangle, ParkingSquare } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useForm, FormProvider, useFormContext, type Resolver, type UseFormSetValue } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Checkbox from '@/components/forms/checkbox';
import PostalCodeCity from '@/components/forms/postal-code-city';
import Text from '@/components/forms/text';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useChangeAddress } from '@/hooks/api/use-amendments';
import useIsPWA from '@/hooks/use-is-pwa';
import { isVAEContract } from '@/lib/contracts';
import { demenagementSchema, type DemenagementValues } from '@/schemas/demenagement';
import type { Contract } from '@/types/api/contract';
import { getAddress } from '@/lib/helpers';

/** Adresse proposée par le web service d'adresses. */
interface AddressSuggestion {
    localityId?: string;
    additionalInfo_1?: string;
    buildingName?: string;
    inputOutput?: string;
}

type FetchAddress = (
    value: string,
    setValue: UseFormSetValue<DemenagementValues>,
    where: 'city' | 'address',
    values?: DemenagementValues,
) => Promise<void>;

const CheckVilleCP = (props: { setValue: UseFormSetValue<DemenagementValues>; fetchAddress: FetchAddress }) => {
    const { watch } = useFormContext<DemenagementValues>();
    const { setValue, fetchAddress } = props

    useEffect(() => {
        const villeNew = watch('villeNew');
        const codePostalNew = watch('codePostalNew');
        if(villeNew && codePostalNew){
            void fetchAddress(`${villeNew} ${codePostalNew}`, setValue, 'city')
        }
    }, [watch('villeNew'), watch('codePostalNew')]);

    return null;
}

export interface DemenagementProps {
    contract: Contract;
}

const Demenagement = ({ contract }: DemenagementProps) => {
    const { mutate: postAmendmentChangeAddress, isPending: loading } = useChangeAddress();
    const initialValues: DemenagementValues = {
        codePostalA: contract.vehicule?.lieuStationnementCodePostal,
        villeA: contract.vehicule?.lieuStationnementVille,
        adresseNew: '',
        adresse2New: '',
        adresse3New: '',
        codePostalNew: '',
        villeNew: '',
        new_adress_is_new_lieuStationnement: true,
        cp_new_lieuStationnement: '',
        ville_new_lieuStationnement: '',
        contrat: contract.id,
        codeProduit: contract.vehicule?.codeProduit,
        sansStationnement: isVAEContract(contract)
    };
    const isPWA = useIsPWA();
    const [ listAddresses, setListAddresses ] = useState<AddressSuggestion[]>([])
    const [ addressSelected, setAddressSelected ] = useState<AddressSuggestion | null>(null)
    const [ adresseNewIsTooLong, setAdresseNewIsTooLong ] = useState<boolean | null>(null)
    const [hasSearchAddress, setHasSearchAddress] = useState(false)
    const [localityId, setLocalityId] = useState<string | null>(null);
    const navigate = useNavigate();

    const methods = useForm<DemenagementValues>({
        defaultValues: initialValues,
        resolver: zodResolver(demenagementSchema) as unknown as Resolver<DemenagementValues>,
    });

    const { register, handleSubmit, watch, formState: { errors }, setValue } = methods;
    const values = watch();

    const fetchAddress: FetchAddress = async (value, setValue, where, values) => {
        setAddressSelected(null)
        let address: AddressSuggestion[];

        if (where === 'city') {
            address = await getAddress<AddressSuggestion[]>(value, 'Undefined')
        } else {
            address = await getAddress<AddressSuggestion[]>(
                value,
                'Undefined',
                localityId,
                values?.codePostalNew,
                values?.villeNew
            )
        }

        if (where === 'city') {
            setLocalityId(address[0]?.localityId ?? null);
            setListAddresses([])
            const adresseFields = ['adresseNew', 'adresse2New', 'adresse3New'] as const;

            adresseFields.forEach(field => {
                setValue(field, null);
            });
        } else {
            setListAddresses(address?.slice(0, 15));
        }
        setHasSearchAddress(true)

        if (value.length > 31){
            setValue('addressNotFound', true)
            setAdresseNewIsTooLong(true)
        } else {
            setAdresseNewIsTooLong(false)
        }

    }

    const selectAddr = (address: AddressSuggestion, setValue: UseFormSetValue<DemenagementValues>) => {
        setAddressSelected(address)
        setValue('adresseNew', address.additionalInfo_1 ?? null)
        setValue('adresse3New', address.buildingName || '')
    }

    const myOnSubmit = (values: DemenagementValues) => {
        postAmendmentChangeAddress({contractId: contract.id, form: values, successFunction: () => navigate(`/mes-contrats/mon-contrat/${values.contrat}`)})
        if(hasSearchAddress){
            void getAddress(values.adresseNew ?? '', "ValidateForm")
        }
    }

    return (
        <div className="space-y-6">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-primary p-8 text-white mt-12">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-sm opacity-90 mb-1">Mon contrat</p>
                    <h1 className="text-3xl font-semibold flex items-center gap-3">
                        <Truck className="w-8 h-8" />
                        Je déménage
                    </h1>
                    <p className="text-sm opacity-80 mt-2">Votre nouvelle adresse sera utilisée pour l'envoi de l'ensemble des documents liés à votre contrat d'assurance.</p>
                </div>
            </div>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(values => myOnSubmit(values))} className="space-y-6">
                    {/* Nouvelle adresse */}
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Home className="w-5 h-5 text-primary" />
                                Nouvelle adresse
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-5">
                            <PostalCodeCity
                                label={isPWA ? 'Votre code postal, ville' : 'Votre code postal, ville (ex: 75011 PARIS)'}
                                cp='codePostalNew'
                                ville='villeNew'
                                required
                                error={errors.codePostalNew?.message}
                                defaultValue={`${values.codePostalNew && values.villeNew ? `${values.codePostalNew} ${values.villeNew}` : ''}`}
                            />

                            <Text
                                type={'text'}
                                label={isPWA ? 'Votre nouvelle adresse' : 'Votre nouvelle adresse (ex: 37 BIS RUE DE LA GARE)'}
                                {...register('adresseNew')}
                                functionOnChange={(e) => void fetchAddress(e, setValue, 'address', values)}
                                value={values.adresseNew}
                                setValue={setValue}
                                required
                            />

                            <Checkbox
                                label={'Je ne trouve pas mon adresse'}
                                {...register('addressNotFound')}
                                checked={values.addressNotFound}
                            />

                            {adresseNewIsTooLong && (
                                <Alert variant="warning">
                                    <AlertTriangle className="w-4 h-4" />
                                    <AlertDescription>
                                        L'adresse dépasse le nombre de caractères autorisé (31). Merci de rentrer manuellement l'adresse et d'utiliser le complément d'adresse.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Liste des adresses suggérées */}
                            {!adresseNewIsTooLong && !values.addressNotFound && !addressSelected && listAddresses.length > 0 && (
                                <div className="border border-border rounded-lg overflow-hidden">
                                    <div className="px-6 py-2 bg-muted/50 border-b border-border">
                                        <p className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                                            <Search className="w-3.5 h-3.5" />
                                            Sélectionnez votre adresse
                                        </p>
                                    </div>
                                    {listAddresses.map((adresse, i) => (
                                        <button
                                            type="button"
                                            key={i}
                                            onClick={() => selectAddr(adresse, setValue)}
                                            className="w-full px-6 py-2.5 text-left text-sm cursor-pointer hover:bg-primary/5 transition-colors border-b border-border last:border-b-0"
                                        >
                                            {adresse.inputOutput}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Complément d'adresse */}
                            {(addressSelected || values.addressNotFound) && (
                                <div className="space-y-5 pt-2">
                                    <Text
                                        type={'text'}
                                        label={isPWA ? "Complément d'adresse" : 'Complément d\'adresse (ex: Appartement 25)'}
                                        {...register('adresse2New')}
                                        value={values.adresse2New}
                                    />

                                    <Text
                                        type={'text'}
                                        label={isPWA ? 'Bâtiment, escalier, étage' : 'Bâtiment, escalier, étage (ex: ENTREE F BATIMENT 5)'}
                                        {...register('adresse3New')}
                                        value={values.adresse3New}
                                        disabled={!!addressSelected && !values.addressNotFound}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Lieu de stationnement */}
                    {!values.sansStationnement && (
                        <Card>
                            <CardHeader className="pb-6 border-b">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <ParkingSquare className="w-5 h-5 text-primary" />
                                    Lieu de stationnement
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 space-y-5">
                                <Checkbox
                                    label={'Cette adresse est également l\'adresse de stationnement'}
                                    {...register('new_adress_is_new_lieuStationnement')}
                                    checked={values.new_adress_is_new_lieuStationnement}
                                />

                                {!values.new_adress_is_new_lieuStationnement && (
                                    <PostalCodeCity
                                        label={'Code postal, ville de stationnement'}
                                        cp='cp_new_lieuStationnement'
                                        ville='ville_new_lieuStationnement'
                                    />
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Action */}
                    <Button
                        type="submit"
                        className="w-full cursor-pointer"
                        disabled={adresseNewIsTooLong ?? undefined}
                        loading={loading}
                        loadingText="Validation en cours..."
                    >
                        <Truck className="w-4 h-4" />
                        Je valide ma nouvelle adresse
                    </Button>

                    <CheckVilleCP fetchAddress={fetchAddress} setValue={setValue} />
                </form>
            </FormProvider>
        </div>
    );
};

export default Demenagement;
